import React, { useState, useEffect, useRef, useMemo } from "react";
import { FiMonitor, FiTablet, FiSmartphone, FiX, FiChevronDown, FiChevronRight, FiRotateCcw } from "react-icons/fi";
import styles from "./VisualEditor.module.css";
import ColorPicker from "../Theme/ColorPicker";
import { THEME_COLOR_KEYS, COLOR_GROUPS, KEY_TO_CSS_VAR } from "./colorKeys";

/**
 * VisualEditor — split-pane WYSIWYG color customizer.
 *
 * Left pane: iframe → /template-preview/{templateName}?editor=1&uid={uid}
 * Right pane: grouped color picker rows (33 keys)
 * postMessage bridge: each picker change posts {type:'updateColor', key, value} to the iframe;
 * the editor-bridge.js script in the storefront applies it to :root via setProperty().
 *
 * Save: PUT /set-store-template-colors/{uid} (existing endpoint), backend invalidates Redis + nginx.
 */
function VisualEditor({ template, storeTemplate, uid, onSave, onCancel }) {
    const iframeRef = useRef(null);
    const [colors, setColors] = useState({});
    const [originalColors, setOriginalColors] = useState({});
    const [openGroup, setOpenGroup] = useState("Header");
    const [saving, setSaving] = useState(false);
    const [iframeReady, setIframeReady] = useState(false);
    const [device, setDevice] = useState("desktop"); // desktop / tablet / mobile

    // Build the full color set: preset values overlaid with the seller's custom overrides.
    useEffect(() => {
        const preset =
            template.colors?.find((c) => c.name === storeTemplate?.activeColorPreset) ||
            template.colors?.find((c) => c.isDefault) ||
            template.colors?.[0];
        const base = preset?.values || {};
        const custom = storeTemplate?.customColors || {};
        const merged = { ...base };
        for (const [k, v] of Object.entries(custom)) {
            if (v != null && v !== "") merged[k] = v;
        }
        setColors(merged);
        setOriginalColors(merged);
    }, [template, storeTemplate]);

    // Listen for messages from the iframe
    useEffect(() => {
        function onMessage(event) {
            const data = event.data;
            if (!data || typeof data !== "object") return;
            if (data.type === "editor-ready") {
                setIframeReady(true);
                // Push current colors to the iframe so any in-progress overrides survive a reload
                if (iframeRef.current?.contentWindow) {
                    iframeRef.current.contentWindow.postMessage(
                        { type: "updateColors", colors },
                        "*"
                    );
                }
            } else if (data.type === "elementClicked") {
                // Phase 2: jump to the matching row & expand its group
                const meta = THEME_COLOR_KEYS.find((k) => k.key === data.key);
                if (meta) {
                    setOpenGroup(meta.group);
                    setTimeout(() => {
                        const el = document.getElementById(`color-row-${data.key}`);
                        if (el) {
                            el.scrollIntoView({ behavior: "smooth", block: "center" });
                            el.classList.add(styles.flash);
                            setTimeout(() => el.classList.remove(styles.flash), 1200);
                        }
                    }, 250);
                }
            }
        }
        window.addEventListener("message", onMessage);
        return () => window.removeEventListener("message", onMessage);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [colors]);

    function pushColorToIframe(key, value) {
        if (iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.postMessage(
                { type: "updateColor", key, value },
                "*"
            );
        }
    }

    function handleColorChange(key, value) {
        setColors((prev) => ({ ...prev, [key]: value }));
        pushColorToIframe(key, value);
    }

    function handleResetKey(key) {
        const original = originalColors[key];
        if (original != null) {
            setColors((prev) => ({ ...prev, [key]: original }));
            pushColorToIframe(key, original);
        }
    }

    function handleDiscardAll() {
        setColors(originalColors);
        if (iframeRef.current?.contentWindow) {
            iframeRef.current.contentWindow.postMessage(
                { type: "updateColors", colors: originalColors },
                "*"
            );
        }
    }

    async function handleSave() {
        setSaving(true);
        try {
            // Save only the keys that differ from the active preset (so seller can revert by switching presets)
            const preset =
                template.colors?.find((c) => c.name === storeTemplate?.activeColorPreset) ||
                template.colors?.find((c) => c.isDefault) ||
                template.colors?.[0];
            const presetVals = preset?.values || {};
            const customColors = {};
            for (const [k, v] of Object.entries(colors)) {
                if (v !== presetVals[k]) customColors[k] = v;
            }
            await onSave(customColors);
            setOriginalColors(colors);
            // Reload iframe to confirm persisted state
            if (iframeRef.current) {
                // eslint-disable-next-line no-self-assign
                iframeRef.current.src = iframeRef.current.src;
            }
        } finally {
            setSaving(false);
        }
    }

    const dirty = useMemo(() => {
        for (const key of Object.keys(colors)) {
            if (colors[key] !== originalColors[key]) return true;
        }
        return false;
    }, [colors, originalColors]);

    const previewUrl = `/template-preview/${template.name}?editor=1${uid ? `&uid=${uid}` : ""
        }${storeTemplate?.activeColorPreset ? `&preset=${encodeURIComponent(storeTemplate.activeColorPreset)}` : ""}`;

    const iframeWidth =
        device === "mobile" ? 390 : device === "tablet" ? 768 : "100%";

    return (
        <div className={styles.container}>
            <div className={styles.toolbar}>
                <div className={styles.toolbarLeft}>
                    <strong>Visual Editor</strong>
                    <span className={styles.templateBadge}>
                        {template.label}
                        {storeTemplate?.activeColorPreset
                            ? ` · ${storeTemplate.activeColorPreset}`
                            : ""}
                    </span>
                </div>
                <div className={styles.deviceToggle}>
                    {["desktop", "tablet", "mobile"].map((d) => (
                        <button
                            key={d}
                            className={`${styles.deviceBtn} ${device === d ? styles.deviceBtnActive : ""
                                }`}
                            onClick={() => setDevice(d)}
                            title={d}
                        >
                            {d === "desktop" ? <FiMonitor /> : d === "tablet" ? <FiTablet /> : <FiSmartphone />}
                            <span>{d}</span>
                        </button>
                    ))}
                </div>
                <div className={styles.toolbarRight}>
                    <button
                        className={styles.discardBtn}
                        onClick={handleDiscardAll}
                        disabled={!dirty || saving}
                    >
                        Discard
                    </button>
                    <button
                        className={styles.saveBtn}
                        onClick={handleSave}
                        disabled={!dirty || saving}
                    >
                        {saving ? "Saving…" : dirty ? "Save Changes" : "Saved"}
                    </button>
                    {onCancel && (
                        <button className={styles.closeBtn} onClick={onCancel} title="Close editor">
                            <FiX />
                        </button>
                    )}
                </div>
            </div>

            <div className={styles.body}>
                <div className={styles.previewPane}>
                    <div
                        className={styles.iframeWrap}
                        style={{ width: iframeWidth, maxWidth: "100%" }}
                    >
                        <iframe
                            ref={iframeRef}
                            src={previewUrl}
                            title="Storefront Preview"
                            className={styles.iframe}
                        />
                        {!iframeReady && <div className={styles.iframeLoading}>Loading preview…</div>}
                    </div>
                </div>

                <div className={styles.editorPane}>
                    <p className={styles.hint}>
                        Pick any color below — the preview updates instantly. Click <strong>Save Changes</strong> to publish.
                    </p>
                    {COLOR_GROUPS.map((group) => {
                        const keys = THEME_COLOR_KEYS.filter((k) => k.group === group);
                        const isOpen = openGroup === group;
                        return (
                            <div key={group} className={styles.group}>
                                <button
                                    className={styles.groupHeader}
                                    onClick={() => setOpenGroup(isOpen ? "" : group)}
                                >
                                    <span>{group}</span>
                                    <span className={styles.groupCount}>{keys.length}</span>
                                    <span className={styles.groupChevron}>{isOpen ? <FiChevronDown /> : <FiChevronRight />}</span>
                                </button>
                                {isOpen && (
                                    <div className={styles.groupBody}>
                                        {keys.map((meta) => {
                                            const value = colors[meta.key] || meta.defaultValue;
                                            const changed = value !== originalColors[meta.key];
                                            return (
                                                <div
                                                    key={meta.key}
                                                    id={`color-row-${meta.key}`}
                                                    className={`${styles.row} ${changed ? styles.rowChanged : ""
                                                        }`}
                                                >
                                                    <div className={styles.rowLabel}>
                                                        <span>{meta.label}</span>
                                                        <code className={styles.rowKey}>{meta.key}</code>
                                                    </div>
                                                    <div className={styles.rowControls}>
                                                        <ColorPicker
                                                            color={value}
                                                            onChange={(v) => handleColorChange(meta.key, v)}
                                                        />
                                                        {changed && (
                                                            <button
                                                                className={styles.resetRow}
                                                                onClick={() => handleResetKey(meta.key)}
                                                                title="Reset to original"
                                                            >
                                                                <FiRotateCcw />
                                                            </button>
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                    </div>
                                )}
                            </div>
                        );
                    })}
                </div>
            </div>
        </div>
    );
}

// Avoid unused-import warning for KEY_TO_CSS_VAR (kept for parity with the EJS bridge).
// eslint-disable-next-line no-unused-expressions
KEY_TO_CSS_VAR;

export default VisualEditor;
